/** 通用 UI 原子组件：所有页面共用，保证视觉一致（暗底 + 霓虹 + 奶白卡片）。 */
import { useEffect, useState, type ReactNode } from "react";
import { api, errorText } from "../lib/api.ts";

export { errorText };

export function Button({
  children,
  variant = "primary",
  className = "",
  ...rest
}: { children: ReactNode; variant?: "primary" | "ghost" | "danger" } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const cls = variant === "primary" ? "btn-primary" : variant === "danger" ? "btn-danger" : "btn-ghost";
  return (
    <button type="button" {...rest} className={`${cls} ${className}`}>
      {children}
    </button>
  );
}

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <section className={`card ${className}`}>{children}</section>;
}

export function Chip({
  children,
  active = false,
  onClick,
  title,
}: {
  children: ReactNode;
  active?: boolean;
  onClick?: () => void;
  title?: string;
}) {
  const cls = `chip ${active ? "chip-active" : ""} ${onClick ? "cursor-pointer hover:border-neon/60" : ""}`;
  if (!onClick) {
    return (
      <span className={cls} title={title}>
        {children}
      </span>
    );
  }
  return (
    <button type="button" className={cls} onClick={onClick} title={title}>
      {children}
    </button>
  );
}

export function Badge18() {
  return <span className="badge-18">18+</span>;
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-[11px] text-cream-dim/70">{hint}</span> : null}
    </label>
  );
}

export function Select({ children, ...rest }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...rest} className="input appearance-none">
      {children}
    </select>
  );
}

export function Notice({ kind = "info", children }: { kind?: "info" | "error" | "ok"; children: ReactNode }) {
  const color =
    kind === "error"
      ? "border-danger/50 bg-danger/10 text-danger"
      : kind === "ok"
        ? "border-ok/40 bg-ok/10 text-ok"
        : "border-line bg-void-2/70 text-cream-dim";
  return <div className={`rounded-xl border px-3 py-2 text-xs ${color}`}>{children}</div>;
}

export function Modal({
  open,
  title,
  onClose,
  children,
  size = "md",
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** md（默认，窄表单）/ lg（裁剪、价格表这类要宽一点的） */
  size?: "md" | "lg";
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/70 p-4 backdrop-blur-sm sm:items-center">
      <div
        className={`max-h-[92dvh] w-full ${size === "lg" ? "max-w-3xl" : "max-w-md"} overflow-y-auto rounded-2xl border border-line bg-panel p-4 shadow-2xl`}
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-bold text-cream">{title}</h3>
          <button type="button" className="text-cream-dim hover:text-neon" onClick={onClose} aria-label="关闭">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Spinner({ label = "加载中…" }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-sm text-cream-dim">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-neon border-t-transparent" />
      {label}
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <div className="rounded-2xl border border-dashed border-line px-4 py-8 text-center text-sm text-cream-dim">{children}</div>;
}

export function useAsync<T>(loader: () => Promise<T>, deps: unknown[]): { data: T | null; error: string | null; loading: boolean; reload: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    loader()
      .then((value) => {
        if (alive) {
          setData(value);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (alive) setError(errorText(err));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  return { data, error, loading, reload: () => setTick((n) => n + 1) };
}

/** 举报入口：每张卡/每条弹幕都能举报（合规硬要求）。 */
export function ReportButton({ targetType, targetId, label = "举报" }: { targetType: "card" | "danmaku" | "room" | "user"; targetId: string; label?: string }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("内容不适");
  const [detail, setDetail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");
  const [message, setMessage] = useState("");

  const submit = async () => {
    setState("sending");
    try {
      await api.report({ targetType, targetId, reason, detail: detail.trim() || undefined });
      setState("done");
      setMessage("已收到举报，管理员会处理。");
    } catch (error) {
      setState("error");
      setMessage(errorText(error));
    }
  };

  return (
    <>
      <button
        type="button"
        className="text-[11px] text-cream-dim/70 underline decoration-dotted hover:text-danger"
        onClick={() => {
          setState("idle");
          setOpen(true);
        }}
      >
        {label}
      </button>
      <Modal open={open} title="举报内容" onClose={() => setOpen(false)}>
        {state === "done" ? (
          <div className="space-y-3">
            <Notice kind="ok">{message}</Notice>
            <Button onClick={() => setOpen(false)}>知道了</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <Field label="原因">
              <Select value={reason} onChange={(event) => setReason(event.target.value)}>
                <option value="内容不适">内容不适</option>
                <option value="涉嫌违法">涉嫌违法</option>
                <option value="骚扰辱骂">骚扰辱骂</option>
                <option value="广告刷屏">广告刷屏</option>
                <option value="其他">其他</option>
              </Select>
            </Field>
            <Field label="补充说明（可选）">
              <textarea
                className="input h-20 resize-none"
                value={detail}
                maxLength={200}
                onChange={(event) => setDetail(event.target.value)}
                placeholder="发生了什么？"
              />
            </Field>
            <p className="text-[11px] text-cream-dim/70">
              目标：{targetType} / {targetId}
            </p>
            {state === "error" ? <Notice kind="error">{message}</Notice> : null}
            <div className="flex gap-2">
              <Button onClick={submit} disabled={state === "sending"}>
                {state === "sending" ? "提交中…" : "提交举报"}
              </Button>
              <Button variant="ghost" onClick={() => setOpen(false)}>
                取消
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
